<template>
  <StepUpAuthenticationOverlay v-if="persistentLoginObj.showAuthenticationOverlay" :params="paramsObj" />
  <section class="wallet-section margin-bottom-xl">
    <div class="grid-x margin-bottom-s align-middle">
      <h3 class="wallet-heading cell shrink margin-right-xs">
        {{ WALLET.heading }}
      </h3>
      <div class="cell auto">
        <Button
          id="add-a-card"
          :aria-disabled="isWalletCardLimitReached()"
          :disabled="isWalletCardLimitReached()"
          :variant="'link'"
          class="secondary-cta"
          :class="{ 'disable-add-payment': isWalletCardLimitReached() }"
          @click="addCard()"
        >
          {{ WALLET.addCardLabel }}
        </Button>
      </div>
    </div>
    <div v-if="creditCards === null">
      <Skeleton height="160px" />
    </div>
    <div v-else :class="{ 'acc-sect-wrapper': getWalletCards.length === 0 }">
      <Notification
        v-if="walletError.notification.message || creditForm.notification.message"
        :severity="walletError.notification.type || creditForm.notification.type"
        :closable="isDefaultCardTypeError"
        :class="{ 'margin-bottom-s': !isTypeError || !isDefaultCardTypeError }"
        @close="walletStore.resetNotificationMessage()"
      >
        <div class="cell auto">
          <div v-if="isTypeError" class="notification-heading">
            {{ WALLET.serviceInterruptionLabel }}
          </div>
          <div v-if="isDefaultCardTypeError" class="notification-text">
            {{ creditForm.notification.message[0] }}
          </div>
          <div class="notification-text">
            <p>{{ walletError.notification.message }}</p>
          </div>
        </div>
      </Notification>

      <template v-if="!isTypeError || !isDefaultCardTypeError">
        <EmptyWallet v-if="walletCardsList.length === 0" />
        <Carousel v-else :key="walletCardsList.length" :list="walletCardsList">
          <template #item="slotProps">
            <!-- TO DO: Enable this when acct balance is live-->
            <!-- <template v-if="slotProps.data === 'verify'">
              <EmailVerifyCard />
            </template> -->
            <!-- <template v-else> -->
            <WalletCard :card="slotProps.data" :persistent-login-obj="persistentLoginObj" @calling-step-up-overlay="callFromWalletCardComp" />
            <!-- </template> -->
          </template>
        </Carousel>
      </template>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUpdated, reactive } from 'vue';
import { storeToRefs } from 'pinia';
import { fireTag } from '@vue-util/tagManager';
import Button from '@atomic-ui/button/Button';
import Carousel from '@atomic-ui/carousel/Carousel';
import Notification from '@atomic-ui/notification/Notification';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import StepUpAuthenticationOverlay from '@nuxt-common/login-component/app/components/StepUpSignInOverlayWrapper';
import { useWalletStore } from '@/stores/useWalletStore';
import { useProfileStore } from '@/stores/useProfileStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useDashboardStore } from '@/stores/useDashboardStore';

import useSlideout from '@/composables/useSlideout';
import populateRememberMe from '@/utils/populateRememberMe';
import { WALLET } from 'config';
import EmptyWallet from './EmptyWallet';
import WalletCard from './WalletCard';
// import EmailVerifyCard from './EmailVerifyCard';
import { useUserSessionId } from '@/composables/useUserSessionId';

const walletStore = useWalletStore();
const profileStore = useProfileStore();
const envPropsStore = useEnvPropsStore();
const dashboardStore = useDashboardStore();

const { userSessionIdInit } = useUserSessionId();
const { showSlideout, toggleSlideout } = useSlideout();
const { creditCards, walletError, getWalletCards, creditForm } = storeToRefs(walletStore);
const { profile } = storeToRefs(profileStore);
const { killswitches } = storeToRefs(dashboardStore);
const { deviceType, tokenizationIframeSource, tokenizationPciIframeSrc } = storeToRefs(envPropsStore);

const classicSunsetNotificationTagFired = ref(false);

const persistentLoginObj = reactive({
  persistentLoginEnabled: false,
  showAuthenticationOverlay: false,
});

function closeStepUpOverlay() {
  persistentLoginObj.showAuthenticationOverlay = false;
}

const paramsObj = reactive({
  closeStepUpOverlay,
});

//TO DO: Show this card with digital acct balance exp
// const isEmailUnverified = computed(() => {
//   return !profileStore.profile?.accountVerified;
// });

// const walletCardsList = computed(() => (isEmailUnverified.value ? ['verify', ...getWalletCards.value] : getWalletCards.value));
const walletCardsList = computed(() => getWalletCards.value);

const isTypeError = computed(() => walletError.value.notification?.type === 'error');
const isDefaultCardTypeError = computed(() => creditForm.value.notification?.type === 'error');

const isWalletCardLimitReached = () => creditCards.value && getWalletCards.value.length >= WALLET.cardLimit;

const callFromWalletCardComp = (callbackFn) => {
  paramsObj.callbackFunction = callbackFn;
  persistentLoginObj.showAuthenticationOverlay = true;
};

const onCardSubmit = async (response) => {
  const severity = response?.walletTender?.msg || 'error';
  if (severity === 'success') {
    await walletStore.fetchCreditCards();
    await dashboardStore.fetchRewardsSummary();
    dashboardStore.setGlobalNotification({
      type: 'success',
      closeButton: false,
      message: WALLET.cardSaveSuccess,
    });
  } else {
    const message = response?.walletTender?.error?.message || '';
    dashboardStore.setGlobalNotification({
      type: 'error',
      closeButton: false,
      message,
    });
  }
  toggleSlideout('my-account-slideout');
};

const addCard = (callbackFunction) => {
  if (persistentLoginObj.persistentLoginEnabled && walletStore.extendToken && !callbackFunction) {
    paramsObj.callbackFunction = addCard.bind(this);
    populateRememberMe(profile.value?.email);
    fireTag('view', {
      page_type: 'step up login',
      page_name: 'home wallet add a card',
      ...WALLET.tag.addCard,
    });
    persistentLoginObj.showAuthenticationOverlay = true;
    return;
  }
  if (callbackFunction) {
    walletStore.setExtendToken(false);
  }
  if (!isWalletCardLimitReached()) {
    const component = import('@nuxt-common/payment-cards/app/components/AddEditDeleteCard');
    const props = {
      cardAction: 'add',
      callback: onCardSubmit,
      isOnlyMacysOption: false,
      tokenizationIframeSource: tokenizationIframeSource.value,
      tokenizationPciIframeSrc: tokenizationPciIframeSrc.value,
      deviceType: deviceType.value,
      isCheckboxDisable: getWalletCards.value.length === 0,
      userAddress: profile.value?.shippingAddress,
      userEmail: profile.value?.email,
    };

    showSlideout('my-account-slideout', WALLET.addCreditCardTitle, component, true, props);
    userSessionIdInit();

    fireTag('link', {
      ...WALLET.tag.addCard,
    });
  }
};

onMounted(() => {
  const { persistentLoginEnabled, persistentLoginAccountDashboardEnabled } = killswitches.value || {};
  persistentLoginObj.persistentLoginEnabled = persistentLoginEnabled && persistentLoginAccountDashboardEnabled;
});

onUpdated(() => {
  if (!classicSunsetNotificationTagFired.value) {
    fireTag('link', {
      event_name: 'classic sunset notification',
      classicSunsetNotificationEnabled: walletError.value.notification.title === 'Classic Consolidation',
    });

    classicSunsetNotificationTagFired.value = true;
  }
});
</script>

<style lang="scss" scoped>
.disable-add-payment {
  cursor: not-allowed;
}
.notification-text p {
  margin-bottom: 0;
}

.icon_alignment {
  position: relative;
  top: rem-calc(8px);
}
</style>

<style lang="scss" brand="bcom" scoped>
.wallet-heading {
  text-transform: none;
}
</style>
